import React from "react";
import { BackIconButtonWithText } from "@src/components/atoms/buttons/back-icon-button-with-text/back-icon-button-with-text";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { ManufacturerProductName } from "@src/components/molecules/manufacturer-product-name/manufacturer-product-name";
import { DropAreaDeactivationOverlayContainer } from "@src/components/templates/layouts/drop-area-deactivation-overlay-container/drop-area-deactivation-overlay-container";
import { PillNavigation } from "@src/components/atoms/pill-navigation/pill-navigation";
import {
  useNodeManagerInfoBarPillsNavHook,
  UseNodeNodeManagerInfoBarPillsNavHookProps,
} from "./use-node-manager-info-bar-pills-nav-hook";
import { ThirdSplitContainer } from "@src/components/templates/layouts/third-split-container/third-split-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface NodeManagerInfoBarPillsNavProps {
  useHook?: () => UseNodeNodeManagerInfoBarPillsNavHookProps;
}

export const NodeManagerInfoBarPillsNav = ({
  useHook = useNodeManagerInfoBarPillsNavHook,
}: NodeManagerInfoBarPillsNavProps) => {
  const i18n = useI18N();
  const { backToSourcesList, updateNodeManagerActiveView } = useHook();
  const showOTA = useFeatureFlag("show-ota", false);
  const showShotDataTable = useFeatureFlag("shotdata-table", false);

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const selectedNodeManagerActiveView = useSelector(
    (state: RootState) => state.yourSlice.nodeManagerActiveView,
  );

  const texts = {
    backToSourcesList: i18n("pages.nodeManager.backToSourcesList"),
    navTextTreeStructure: i18n(
      "modules.nodeManagerInfoBarPillsNav.navTextTreeStructure",
    ),
    navTextDashboard: i18n(
      "modules.nodeManagerInfoBarPillsNav.navTextDashboard",
    ),
    navTextConditionMonitoring: i18n(
      "modules.nodeManagerInfoBarPillsNav.navTextConditionMonitoring",
    ),
    navTextOneShot: i18n("modules.nodeManagerInfoBarPillsNav.navTextOneShot"),
    navTextShotDataTable: i18n(
      "modules.nodeManagerInfoBarPillsNav.navTextShotDataTable",
    ),
    navTextOTA: i18n("modules.nodeManagerInfoBarPillsNav.navTextOTA"),
  };

  const checkPermissionsForTreeStructure = (permissions) => {
    const canViewActiveStructure =
      permissions.includes("view-active-nodeset") ||
      permissions.includes("manage-active-nodeset");

    const canViewInitialStructure = permissions.includes(
      "view-initial-server-nodeset",
    );

    return canViewActiveStructure && canViewInitialStructure;
  };

  return (
    <DropAreaDeactivationOverlayContainer>
      <ThirdSplitContainer alignCenter>
        <FlexRowContainer>
          <BackIconButtonWithText
            text={texts.backToSourcesList}
            fun={backToSourcesList}
          />
          <VerticalSeparator />
          <ManufacturerProductName />
        </FlexRowContainer>
        <FlexRowContainer justifyContent="center">
          <PillNavigation
            options={[
              {
                name: texts.navTextTreeStructure,
                link: "treeStructure",
                render: checkPermissionsForTreeStructure(
                  authContext.permissions,
                ),
              },
              { name: texts.navTextDashboard, link: "dashboard", render: true },
              {
                name: texts.navTextConditionMonitoring,
                link: "conditionMonitoring",
                render:
                  authContext.permissions.includes(
                    "view-condition-monitoring",
                  ) ||
                  authContext.permissions.includes(
                    "manage-condition-monitoring",
                  ),
              },
              {
                name: texts.navTextOTA,
                link: "machineSettings",
                render:
                  showOTA &&
                  (authContext.permissions.includes(
                    "initiate-update-process",
                  ) ||
                    authContext.permissions.includes(
                      "approve-update-process",
                    ) ||
                    authContext.permissions.includes("manage-firmware")),
              },
              /* {
                name: texts.navTextOneShot,
                link: "oneShot",
                render:
                  authContext.permissions.includes("view-developer-features") ||
                  authContext.permissions.includes("view-one-shot"),
              }, */
              {
                name: texts.navTextShotDataTable,
                link: "shotDataTable",
                render:
                  showShotDataTable &&
                  authContext.permissions.includes("view-dashboards"),
              },
            ]}
            activeView={selectedNodeManagerActiveView}
            onChangeCallback={updateNodeManagerActiveView}
          />
        </FlexRowContainer>
        <div />
      </ThirdSplitContainer>
    </DropAreaDeactivationOverlayContainer>
  );
};
